import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2, X } from 'lucide-react';
import { getUserTimezone } from '../utils/timezone';
import { NOMINATIM_BASE } from '../utils/nominatim';

const TZ_TO_COUNTRYCODES = {
  'australia': 'au',
  'pacific/': 'au,nz,fj',
  'asia/manila': 'ph',
  'asia/beirut': 'lb',
  'europe/istanbul': 'tr',
  'asia/istanbul': 'tr',
};

function getCountryBias() {
  const tz = (getUserTimezone() || '').toLowerCase();
  if (!tz) return '';
  for (const [prefix, codes] of Object.entries(TZ_TO_COUNTRYCODES)) {
    if (tz.includes(prefix)) return codes;
  }
  if (tz.startsWith('australia/')) return 'au';
  return '';
}

const formatCleanAddress = (suggestion) => {
  const a = suggestion.address;
  if (!a) return suggestion.display_name || '';
  const parts = [];
  const street = [a.house_number, a.road].filter(Boolean).join(' ');
  if (street) parts.push(street);
  const suburb = a.suburb || a.village || a.town || a.hamlet;
  if (suburb) parts.push(suburb);
  if (a.state) parts.push(a.state);
  if (a.postcode) parts.push(a.postcode);
  if (a.country) parts.push(a.country);
  return parts.length ? parts.join(', ') : suggestion.display_name || '';
};

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Search address...',
  className = '',
  inputClassName = '',
  debounceMs = 300,
  limit = 8,
  countrycodes,
  disabled = false,
  dropUp = false,
  onClear,
  'data-testid': testId = 'address-autocomplete',
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const enterPendingRef = useRef(false);
  const lastQueryRef = useRef('');

  const fetchSuggestions = useCallback(async (query, autoSelect = false) => {
    if (!query || query.trim().length < 2) {
      if (query.trim().length === 0) {
        setSuggestions([]);
        setIsOpen(false);
        lastQueryRef.current = '';
      }
      return;
    }

    const trimmed = query.trim();
    const prevQuery = lastQueryRef.current;
    lastQueryRef.current = trimmed;
    setLoading(true);
    try {
      const cc = countrycodes || getCountryBias();
      let url = `${NOMINATIM_BASE}/search?format=json&q=${encodeURIComponent(trimmed)}&limit=${limit}&addressdetails=1&autocomplete=1`;
      if (cc) url += `&countrycodes=${cc}`;

      const resp = await fetch(url);
      const data = await resp.json();
      let results = data && data.length > 0 ? data : [];

      // FALLBACK: If empty and query has 3+ words, the last word might be a partial suburb.
      // Search without the last word, then filter results by suburb/city prefix.
      if (results.length === 0) {
        const words = trimmed.split(/\s+/);
        if (words.length >= 3) {
          const lastWord = words[words.length - 1].toLowerCase();
          const baseQuery = words.slice(0, -1).join(' ');
          const fallbackUrl = `${NOMINATIM_BASE}/search?format=json&q=${encodeURIComponent(baseQuery)}&limit=20&addressdetails=1${cc ? '&countrycodes=' + cc : ''}`;
          const fallbackResp = await fetch(fallbackUrl);
          const fallbackData = await fallbackResp.json();
          if (fallbackData && fallbackData.length > 0) {
            // Filter: keep results where suburb, city, town, or display_name contains the partial word
            results = fallbackData.filter(r => {
              const a = r.address || {};
              const fields = [a.suburb, a.city, a.town, a.village, a.municipality, a.state, r.display_name].filter(Boolean);
              return fields.some(f => f.toLowerCase().includes(lastWord));
            }).slice(0, limit);
          }
        }
      }

      if (results.length > 0) {
        const mapped = results.map(item => ({
          display_name: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
          type: item.type,
          address: item.address,
        }));
        setSuggestions(mapped);
        setHighlightIndex(-1);

        if (autoSelect || enterPendingRef.current) {
          enterPendingRef.current = false;
          onChange(formatCleanAddress(mapped[0]));
          setSuggestions([]);
          setIsOpen(false);
          onSelect(mapped[0]);
        } else {
          setIsOpen(true);
        }
      } else {
        // No results even after fallback — keep or clear based on word count change
        const prevWordCount = prevQuery.split(/\s+/).length;
        const newWordCount = trimmed.split(/\s+/).length;
        if (newWordCount > prevWordCount) {
          setSuggestions([]);
          setIsOpen(false);
        }
        if (enterPendingRef.current) {
          enterPendingRef.current = false;
        }
      }
    } catch {
      if (enterPendingRef.current) {
        enterPendingRef.current = false;
      }
    } finally {
      setLoading(false);
    }
  }, [limit, countrycodes, onChange, onSelect]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);

    if (val.trim().length === 0) {
      setSuggestions([]);
      setIsOpen(false);
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), debounceMs);
  };

  const handleSelect = (suggestion) => {
    onChange(formatCleanAddress(suggestion));
    setSuggestions([]);
    setIsOpen(false);
    setHighlightIndex(-1);
    onSelect(suggestion);
  };

  const handleClear = () => {
    onChange('');
    setSuggestions([]);
    setIsOpen(false);
    onClear?.();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && suggestions.length > 0) {
        if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
          handleSelect(suggestions[highlightIndex]);
        } else {
          handleSelect(suggestions[0]);
        }
      } else if (value.trim()) {
        enterPendingRef.current = true;
        fetchSuggestions(value, true);
      }
      return;
    }

    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightIndex(-1);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const formatTitle = (suggestion) => {
    const a = suggestion.address;
    if (!a) return suggestion.display_name.split(',').slice(0, 2).join(',');
    const parts = [];
    if (a.house_number) parts.push(a.house_number);
    if (a.road) parts.push(a.road);
    if (parts.length > 0) return parts.join(' ');
    return suggestion.display_name.split(',').slice(0, 2).join(',').trim();
  };

  const formatSubtext = (suggestion) => {
    const a = suggestion.address;
    if (!a) return '';
    const parts = [a.suburb, a.city || a.town || a.village, a.state].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      <div className="relative">
        <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
          placeholder={placeholder}
          className={`pl-9 pr-8 ${inputClassName}`}
          disabled={disabled}
          data-testid={`${testId}-input`}
        />
        {loading && (
          <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {value && !disabled && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground"
            data-testid={`${testId}-clear`}
            type="button"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <div
          className={`absolute z-[9999] w-full bg-popover border border-border rounded-md shadow-lg overflow-hidden max-h-72 overflow-y-auto ${
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
          data-testid={`${testId}-dropdown`}
        >
          {suggestions.map((s, i) => (
            <button
              key={`${s.lat}-${s.lon}-${i}`}
              onClick={() => handleSelect(s)}
              onMouseEnter={() => setHighlightIndex(i)}
              className={`w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors ${
                i === highlightIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
              }`}
              data-testid={`${testId}-option-${i}`}
              type="button"
            >
              <div className="font-medium truncate text-foreground">
                {formatTitle(s)}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {formatSubtext(s)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
